/**
 * CacheService
 *
 * Generic caching service for API responses to reduce redundant network requests.
 * Uses in-memory Map storage with configurable TTL (time-to-live) per cache entry.
 *
 * Features:
 * - Generic type support for type-safe cache operations
 * - Configurable TTL per cache entry (default: 5 minutes)
 * - Automatic expiration checking
 * - Clear individual or all cache entries
 *
 * @example
 * ```typescript
 * // Store data with default TTL (5 minutes)
 * cacheService.set('user-list', users);
 *
 * // Store data with custom TTL (10 minutes)
 * cacheService.set('dashboard-data', data, 10);
 *
 * // Retrieve data
 * const cachedUsers = cacheService.get<User[]>('user-list');
 *
 * // Check if key exists and is not expired
 * if (cacheService.has('user-list')) {
 *   // Use cached data
 * }
 *
 * // Clear specific cache entry
 * cacheService.clear('user-list');
 *
 * // Clear all cache
 * cacheService.clear();
 * ```
 */

import { Injectable } from '@angular/core';

/**
 * Interface representing a cache entry with data and expiration time
 */
interface CacheEntry<T> {
  /** The cached data */
  data: T;
  /** Timestamp when the cache entry expires (milliseconds since epoch) */
  expiresAt: number;
}

/**
 * Default time-to-live for cache entries in minutes
 */
const DEFAULT_TTL_MINUTES = 5;

@Injectable({
  providedIn: 'root'
})
export class CacheService {
  /**
   * In-memory cache storage using Map for O(1) operations
   */
  private cache = new Map<string, CacheEntry<unknown>>();

  /**
   * Retrieves data from cache if it exists and is not expired
   *
   * @template T - Type of the cached data
   * @param key - The cache key to retrieve
   * @returns The cached data if found and not expired, null otherwise
   *
   * @example
   * ```typescript
   * const users = cacheService.get<User[]>('users');
   * if (users) {
   *   // Use cached users
   * }
   * ```
   */
  get<T>(key: string): T | null {
    const entry = this.cache.get(key) as CacheEntry<T> | undefined;

    if (!entry) {
      return null;
    }

    // Check if entry has expired
    if (this.isExpired(key)) {
      this.cache.delete(key);
      return null;
    }

    return entry.data;
  }

  /**
   * Stores data in cache with optional TTL
   *
   * @template T - Type of the data to cache
   * @param key - The cache key to store under
   * @param data - The data to cache
   * @param ttlMinutes - Time-to-live in minutes (default: 5 minutes)
   *
   * @example
   * ```typescript
   * // Store with default TTL (5 minutes)
   * cacheService.set('users', userList);
   *
   * // Store with custom TTL (10 minutes)
   * cacheService.set('dashboard', dashboardData, 10);
   * ```
   */
  set<T>(key: string, data: T, ttlMinutes: number = DEFAULT_TTL_MINUTES): void {
    const expiresAt = Date.now() + ttlMinutes * 60 * 1000;
    const entry: CacheEntry<T> = { data, expiresAt };
    this.cache.set(key, entry as CacheEntry<unknown>);
  }

  /**
   * Checks if a cache entry exists and is not expired
   *
   * @param key - The cache key to check
   * @returns true if the entry exists and is not expired, false otherwise
   *
   * @example
   * ```typescript
   * if (cacheService.has('users')) {
   *   const users = cacheService.get<User[]>('users');
   * } else {
   *   // Fetch from API
   * }
   * ```
   */
  has(key: string): boolean {
    if (!this.cache.has(key)) {
      return false;
    }

    // Check expiration
    if (this.isExpired(key)) {
      this.cache.delete(key);
      return false;
    }

    return true;
  }

  /**
   * Clears cache entries
   *
   * @param key - Optional key to clear specific entry. If omitted, clears all cache
   *
   * @example
   * ```typescript
   * // Clear specific entry
   * cacheService.clear('users');
   *
   * // Clear all cache
   * cacheService.clear();
   * ```
   */
  clear(key?: string): void {
    if (key) {
      this.cache.delete(key);
    } else {
      this.cache.clear();
    }
  }

  /**
   * Checks if a cache entry has expired
   *
   * @param key - The cache key to check
   * @returns true if the entry exists and has expired, false otherwise
   *
   * @example
   * ```typescript
   * if (cacheService.isExpired('users')) {
   *   // Refetch data
   * }
   * ```
   */
  isExpired(key: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) {
      return false;
    }

    return Date.now() > entry.expiresAt;
  }

  /**
   * Gets the number of entries in the cache
   *
   * @returns The number of cache entries
   */
  get size(): number {
    return this.cache.size;
  }

  /**
   * Gets all cache keys
   *
   * @returns Array of all cache keys
   */
  getKeys(): string[] {
    return Array.from(this.cache.keys());
  }
}
